import { useState, useCallback, useEffect } from "react";
import { useAuth } from "shared/contexts/AuthProvider";
import { useNotification } from "shared/contexts/UserNotificationProvider";
import { useTranslation } from "react-i18next";
import { robotsService } from "features/administration/services";
import { robotPeerConnectionsService } from "shared/services/robot-services/robotPeerConnectionsService";

// Hook to manage robot info and clients for teleoperation control page
export const useRobotControlManager = (robotId, options={}) => {
  const { authFetch } = useAuth();
  const { notifySuccess, notifyError } = useNotification();
  const { t } = useTranslation();

  const [robotInfo, setRobotInfo] = useState(null);
  const [robotClients, setRobotClients] = useState([]);
  const [loading, setLoading] = useState(false);

  // webrtc management functions


  const loadRobotInfo = useCallback(
    async (robotId) => {
      try {
        setLoading(true);
        const response = await robotsService.getById(authFetch, robotId);
        setRobotInfo(response.data);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        notifyError(t("teleop.robotInfo.loadFailed", { message: errorMessage }));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, notifyError, t],
  );

  const loadRobotClientsInfo = useCallback(
    async (robotId) => {
      try {
        setLoading(true);
        const response = await robotPeerConnectionsService.getRobotClientsByRobotId(authFetch, robotId);
        setRobotClients(response.data);
        return response.data;
      } catch (error) {
        const errorMessage = error?.response?.data?.detail || error.message;
        notifyError(t("teleop.robotInfo.loadPeersFailed", { message: errorMessage }));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [authFetch, notifyError, t],
  );

  const loadData = useCallback(async (robotId) => {
    if (!robotId) {
      notifyError(t("teleop.robotInfo.noRobotId"));
      return;
    }
    await loadRobotInfo(robotId);
    await loadRobotClientsInfo(robotId);
  }, [loadRobotInfo, loadRobotClientsInfo, notifyError, t]);

  useEffect(() => {
    if (!robotId) {      
      return;
    }    
    loadData(robotId);
  }, [robotId, loadData]);

  return {
    robotInfo,
    robotClients,
    loading,
    loadRobotInfo,    
    loadRobotClientsInfo,
    loadData,
  };
};
